import type { ReactNode } from 'react';
import type { Experiment } from 'types';
import type { Capability } from 'definitions/capabilities';
import type { SetupStep, SetupStepKey } from './home-states';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from 'hooks/useAuth';
import { can } from 'definitions/capabilities';
import { cn } from 'helpers/cn';
import { Panel } from '../ConsolePanel';
import { ScriptsModal } from '../Websites/List/ScriptsModal';
import { CreateExperimentModal } from '../Experiments/Library/CreateExperimentModal';
import { currentStep, latestDraft, setupSteps } from './home-states';

type StepCopy = { title: string; body: string; doneBody?: string; needs?: Capability };

const STEP_COPY: Record<SetupStepKey, StepCopy> = {
  website: {
    title: 'Add your website',
    body: 'Experiments run on the websites you add here.',
    needs: 'websites:manage',
  },
  snippet: {
    title: 'Add the tracking snippet',
    body: "Paste one script tag into your site's <head>. It loads your experiments and records what visitors do.",
    doneBody: 'Confirmed: this website has recorded visitors.',
  },
  create: {
    title: 'Create an experiment',
    body: 'Pick a page and describe the change you want to test. It stays a draft until you start it.',
    doneBody: 'You have a draft ready.',
    needs: 'experiments:create',
  },
  start: {
    title: 'Start it',
    body: 'Open the draft and start it. Traffic splits between the variants, and the first numbers appear here within minutes.',
    needs: 'experiments:start',
  },
};

type GetStartedPanelProps = {
  websiteId: number | undefined;
  experiments: readonly Experiment[];
};

/**
 * Home for a website with nothing live: the steps from an empty dashboard to a running
 * experiment, each marked done from what already exists rather than from a checkbox.
 *
 * A step the reader cannot take says who can, instead of offering a button the server
 * would refuse.
 */
export const GetStartedPanel = ({ websiteId, experiments }: GetStartedPanelProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  const steps = setupSteps({ hasWebsite: !!websiteId, experiments });
  const current = currentStep(steps);
  const draft = latestDraft(experiments);

  const actionFor = (step: SetupStep): ReactNode => {
    const copy = STEP_COPY[step.key];
    if (step.done) return null;
    // Everything after the website happens on one, so those steps wait for it.
    if (!websiteId && step.key !== 'website') return null;
    if (copy.needs && !can(user, copy.needs)) {
      return <span className="supporting text-nowrap">An Owner or Admin can do this.</span>;
    }

    const variant = step.key === current ? 'default' : 'outline';
    const button = (label: string, onClick: () => void) => (
      <Button size="sm" variant={variant} onClick={onClick} className="h-8 rounded-lg">
        {label}
      </Button>
    );

    if (step.key === 'website') return button('Add website', () => navigate('/websites/list'));
    if (step.key === 'snippet') {
      return websiteId ? (
        <ScriptsModal websiteId={websiteId} renderTrigger={(open) => button('Show the snippet', open)} />
      ) : null;
    }
    if (step.key === 'create') return button('Create experiment', () => setIsCreating(true));
    if (step.key === 'start' && draft) return button('Open draft', () => navigate(`/experiments/${draft.id}`));
    return null;
  };

  return (
    <Panel
      title="Get your first test live"
      description="A few steps from an empty dashboard to a running experiment."
      tourAnchor="home-get-started"
      bodyClassName="p-0"
      footer={
        <p className="supporting m-0 sm:mr-auto">
          A new experiment is a draft. It only reaches visitors once you start it.
        </p>
      }
    >
      <ol className="divide-line m-0 list-none divide-y p-0">
        {steps.map((step, index) => {
          const copy = STEP_COPY[step.key];
          return (
            <li
              key={step.key}
              className="flex flex-col gap-2.5 px-3.5 py-3 sm:flex-row sm:items-center sm:gap-3.5"
              aria-current={step.key === current ? 'step' : undefined}
            >
              <div className="flex min-w-0 flex-1 items-start gap-2.5">
                <span
                  className={cn(
                    'num flex size-5 shrink-0 items-center justify-center rounded-full border text-nano',
                    step.done ? 'border-ok bg-ok text-white' : 'border-line text-dim',
                    step.key === current && 'border-accent text-accent',
                  )}
                  aria-hidden="true"
                >
                  {step.done ? <Check className="size-3" strokeWidth={2.5} /> : index + 1}
                </span>
                <div className="min-w-0">
                  <p className={cn('m-0 text-meta font-medium', step.done ? 'text-dim' : 'text-foreground')}>
                    {copy.title}
                    {step.done && <span className="sr-only"> (done)</span>}
                  </p>
                  <p className="supporting m-0">{step.done ? (copy.doneBody ?? copy.body) : copy.body}</p>
                </div>
              </div>
              <div className="pl-7 sm:pl-0">{actionFor(step)}</div>
            </li>
          );
        })}
      </ol>
      <CreateExperimentModal isCreating={isCreating} setIsCreating={setIsCreating} />
    </Panel>
  );
};
